import React, { Suspense, useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { Spin, Typography } from 'antd';
import { ControlOutlined } from '@ant-design/icons';
import { useUser } from '../../contexts/UserContext';
import { isLocalBridgeApiEnabled } from '../../services/bridgeSessionDataAdapter';
import { getBridgeSession, listCompanies } from '../../services/localBridgeSessionApi';
import { controlBridgeHomeHref } from '../../utils/controlBridgeRoutes';
import { DashboardNavigationProvider } from '../../contexts/DashboardNavigationContext';
import { DashboardSettingsProvider } from '../../contexts/DashboardSettingsContext';
import { DashboardBreadcrumb } from '../../components/Dashboard/DashboardBreadcrumb';
import { DashboardQuickNav } from '../../components/Dashboard/DashboardQuickNav';
import { DashboardFavorites } from '../../components/Dashboard/DashboardFavorites';
import { DashboardSettings } from '../../components/Dashboard/DashboardSettings';
import { LiveStateIndicator } from '../../components/ControlBridge';
import { OnboardingSnapshotBanner } from '../../components/onboarding/OnboardingSnapshotBanner';
import { ControlBridgeCompanyRequired } from '../../components/onboarding/ControlBridgeCompanyRequired';
import { usePublishSurfaceChatContext } from '../../hooks/usePublishSurfaceChatContext';
import { dashboardSurfaceSummary } from '../../utils/surfaceRouteLabels';
import styles from './HierarchicalDashboard.module.css';

const { Title } = Typography;

// Lazy load dashboard level components for better performance
const BusinessDomainDashboard = React.lazy(() => import('./BusinessDomainDashboard'));
const ProductsOverviewDashboard = React.lazy(() => import('./ProductsOverviewDashboard'));
const ProductDashboard = React.lazy(() => import('./ProductDashboard'));
const OperationsDashboard = React.lazy(() => import('./OperationsDashboard'));
const DevelopmentDashboard = React.lazy(() => import('./DevelopmentDashboard'));
const ProjectionsDashboard = React.lazy(() => import('./ProjectionsDashboard'));
const MarketFitDashboard = React.lazy(() => import('./MarketFitDashboard'));
const MarketFitAnalysisPage = React.lazy(() => import('./MarketFitAnalysisPage'));
const MarketFitSituationPage = React.lazy(() => import('../MarketFitSituationPage'));
const MarketFitSolutionPage = React.lazy(() => import('../MarketFitSolutionPage'));
const StakeholdersDashboard = React.lazy(() => import('./StakeholdersDashboard'));
const CustomersDashboard = React.lazy(() => import('./CustomersDashboard'));
const ObjectivePage = React.lazy(() => import('./ObjectivePage'));
const BridgeCardPage = React.lazy(() => import('./BridgeCardPage'));
const ProductPipelineDesigner = React.lazy(() => import('./ProductPipelineDesigner'));

// ============================================================================
// LOADING COMPONENT
// ============================================================================

const DashboardLoading: React.FC = () => (
  <div className={styles.loadingContainer}>
    <Spin size="large">
      <div className={styles.loadingContent}>Loading dashboard...</div>
    </Spin>
  </div>
);

// ============================================================================
// DASHBOARD HEADER
// ============================================================================

const DashboardHeader: React.FC = () => {
  const { selectedCompany } = useUser();
  const navigate = useNavigate();

  return (
    <header className={styles.header}>
      <div className={styles.headerLeft}>
        <ControlOutlined className={styles.controlIcon} />
        <Title
          level={3}
          className={styles.headerTitle}
          onClick={() => navigate(controlBridgeHomeHref())}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && navigate(controlBridgeHomeHref())}
        >
          {selectedCompany?.name ? `${selectedCompany.name} Control Bridge` : 'Control Bridge'}
        </Title>
      </div>
      <div className={styles.headerActions}>
        <LiveStateIndicator status="connected" compact />
        <DashboardSettings />
        <DashboardQuickNav />
        <DashboardFavorites />
      </div>
    </header>
  );
};

// ============================================================================
// MAIN DASHBOARD CONTAINER
// ============================================================================

interface HierarchicalDashboardContentProps {
  className?: string;
}

const HierarchicalDashboardContent: React.FC<HierarchicalDashboardContentProps> = ({ className }) => {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const { selectedCompany, selectCompanyForLocalBridge } = useUser();

  const dashboardSurfaceContext = useMemo(() => {
    const companyName = selectedCompany?.name ?? 'Dashboard';
    const summary = dashboardSurfaceSummary(pathname);
    return {
      surfaceId: `dashboard:${selectedCompany?.id ?? 'none'}:${pathname}`,
      surfaceLabel: `${companyName} · ${summary}`,
      route: pathname,
      summary,
      metadata: {
        companyId: selectedCompany?.id,
      },
    };
  }, [pathname, selectedCompany?.id, selectedCompany?.name]);

  usePublishSurfaceChatContext(dashboardSurfaceContext);

  // When navigating from Local Bridge with ?sessionId=X, auto-select company for header
  useEffect(() => {
    const sessionId = searchParams.get('sessionId');
    if (!sessionId || !isLocalBridgeApiEnabled()) return;

    let cancelled = false;
    (async () => {
      const session = await getBridgeSession(sessionId);
      if (cancelled || !session?.company_id) return;
      if (selectedCompany?.id === session.company_id) return; // Already correct
      const companies = await listCompanies();
      const company = companies.find((c) => c.id === session.company_id);
      if (cancelled || !company) return;
      selectCompanyForLocalBridge(company.id, company.name);
    })();
    return () => { cancelled = true; };
  }, [searchParams, selectedCompany?.id, selectCompanyForLocalBridge]);

  return (
    <div className={`${styles.dashboard} ${className || ''}`}>
      <DashboardHeader />
      <ControlBridgeCompanyRequired />
      <OnboardingSnapshotBanner />
      <main className={styles.content}>
        <Suspense fallback={<DashboardLoading />}>
          <Routes>
            {/* Business Level (Root) — deprecated; render Control Bridge is canonical home */}
            <Route index element={<Navigate to={controlBridgeHomeHref()} replace />} />
            
            {/* Domain Views */}
            <Route path="domain/business" element={<BusinessDomainDashboard />} />
            
            {/* Products Overview */}
            <Route path="products" element={<ProductsOverviewDashboard />} />
            
            {/* Product Level */}
            <Route path="product/:productId" element={<ProductDashboard />} />
            
            {/* Product Sub-levels */}
            <Route path="product/:productId/demand-funnel" element={<Navigate to="pipeline" replace />} />
            <Route path="product/:productId/pipeline" element={<ProductPipelineDesigner />} />
            <Route path="product/:productId/operations" element={<OperationsDashboard />} />
            <Route path="product/:productId/development" element={<DevelopmentDashboard />} />
            <Route path="product/:productId/projections" element={<ProjectionsDashboard />} />
            <Route path="product/:productId/marketfit" element={<MarketFitDashboard />} />
            <Route path="product/:productId/marketfit/analysis/:dealId" element={<MarketFitAnalysisPage />} />
            <Route path="product/:productId/marketfit/situation/:situationId" element={<MarketFitSituationPage />} />
            <Route path="product/:productId/marketfit/solution/:solutionId" element={<MarketFitSolutionPage />} />
            
            {/* Stakeholders Level */}
            <Route path="stakeholders" element={<StakeholdersDashboard />} />
            
            {/* Customers Level */}
            <Route path="customers" element={<CustomersDashboard />} />
            
            {/* Objective Page */}
            <Route path="objective/:objectiveId" element={<ObjectivePage />} />
            
            {/* Bridge Card Page */}
            <Route path="bridge-card" element={<BridgeCardPage />} />
            
            {/* Fallback */}
            <Route path="*" element={<Navigate to={controlBridgeHomeHref()} replace />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
};

// ============================================================================
// EXPORTED COMPONENT WITH PROVIDER
// ============================================================================

export const HierarchicalDashboard: React.FC = () => {
  return (
    <DashboardNavigationProvider>
      <DashboardSettingsProvider>
        <HierarchicalDashboardContent />
      </DashboardSettingsProvider>
    </DashboardNavigationProvider>
  );
};

export default HierarchicalDashboard;
